import React, { useState, useEffect } from 'react';
import { Pencil, Mail, Phone, MapPin, Ban, ClipboardList, Key, ExternalLink } from 'lucide-react';
import { contactService } from '../services/contactService';
import { Contact } from '../types/contact';
import { toast } from 'react-toastify';
import * as noteService from '../services/noteService';
import ChangeHistoryButton from './ChangeHistoryButton';
import ChangeHistoryDrawer from './ChangeHistoryDrawer';
import '../styles/ContactDetailsSlider.css';

interface ContactDetailsSliderProps {
  workspaceId: string;
  contactId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const ContactDetailsSlider: React.FC<ContactDetailsSliderProps> = ({
  workspaceId,
  contactId,
  onClose,
  onSuccess
}) => {
  const [contact, setContact] = useState<Contact | null>(null);
  const [deals, setDeals] = useState<any[]>([]);
  const [notes, setNotes] = useState<noteService.Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingTile, setEditingTile] = useState<string | null>(null);
  const [editData, setEditData] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);

  useEffect(() => {
    fetchContact();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const fetchContact = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await contactService.getById(workspaceId, contactId, ['deals']);
      setContact(data.contact);
      setDeals(data.deals || []);
      await fetchNotes();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch contact details');
      console.error('Error fetching contact:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      const fetchedNotes = await noteService.getNotes(workspaceId, 'contact', contactId);
      setNotes(fetchedNotes);
    } catch (err: any) {
      console.error('Error fetching notes:', err);
      // Don't show error to user, just log it
    }
  };

  const refreshContact = async () => {
    try {
      setError('');
      const data = await contactService.getById(workspaceId, contactId, ['deals']);
      setContact(data.contact);
      setDeals(data.deals || []);
      // Don't fetch notes here - they don't change when contact fields are updated
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to refresh contact details');
      console.error('Error refreshing contact:', err);
    }
  };

  const formatAddress = (address: any) => {
    const parts = [
      address.street1,
      address.street2,
      address.city,
      address.state,
      address.zip,
      address.country
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Calculate stats from deals
  const getActiveDealsCount = () => {
    return deals.filter(deal =>
      deal.stage && !['CANCELLED', 'CLOSED', 'TERMINATED'].includes(deal.stage)
    ).length;
  };

  const getTotalSpent = () => {
    return deals.reduce((total, deal) => {
      return total + (deal.finance?.totalPrice || 0);
    }, 0);
  };

  const getUnitsOwned = () => {
    return deals.filter(deal =>
      deal.unitId && !['CANCELLED', 'TERMINATED'].includes(deal.stage)
    ).length;
  };

  const handleEditTile = (tileName: string, data: any) => {
    setEditingTile(tileName);
    setEditData(JSON.parse(JSON.stringify(data))); // Deep clone
  };

  const handleCancelEdit = () => {
    setEditingTile(null);
    setEditData(null);
  };

  const handleSaveTile = async () => {
    if (!contact || !editData) return;

    try {
      setSaving(true);
      setError('');

      if (editingTile === 'internal-notes') {
        // Handle notes separately - always create a new note
        const noteContent = editData.content?.trim();
        if (noteContent) {
          await noteService.createNote(workspaceId, 'contact', contactId, {
            content: noteContent,
            category: 'general'
          });
          await fetchNotes();
          toast.success('Note added successfully');
        }
      } else {
        // Handle contact updates
        const updatePayload: any = {};

        // Build update payload based on which tile is being edited
        if (editingTile === 'geography') {
          updatePayload.geography = editData;
        } else if (editingTile === 'communication') {
          updatePayload.doNotCall = editData.doNotCall;
          updatePayload.doNotEmail = editData.doNotEmail;
          updatePayload.doNotSMS = editData.doNotSMS;
        } else if (editingTile === 'portal') {
          updatePayload.allowLogin = editData.allowLogin;
        } else if (editingTile === 'contact-info') {
          updatePayload.emails = editData.emails.filter((e: any) => e.email.trim());
          updatePayload.phones = editData.phones.filter((p: any) => p.number.trim());
          updatePayload.addresses = editData.addresses;
        } else if (editingTile === 'contact-types') {
          updatePayload.contactType = editData.contactType;
        } else if (editingTile === 'name') {
          updatePayload.prefix = editData.prefix;
          updatePayload.firstName = editData.firstName;
          updatePayload.middleName = editData.middleName;
          updatePayload.lastName = editData.lastName;
          updatePayload.suffix = editData.suffix;
          updatePayload.displayName = editData.displayName;
        }

        await contactService.update(workspaceId, contactId, updatePayload);
        await refreshContact();
      }

      setEditingTile(null);
      setEditData(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update');
      console.error('Error updating:', err);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async () => {
    try {
      setDeleting(true);
      await contactService.delete(workspaceId, contactId);
      if (onSuccess) {
        onSuccess(); // Refresh the contacts list
      }
      onClose(); // Close slider after successful deletion
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete contact');
      console.error('Error deleting contact:', err);
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleResetPasswordClick = () => {
    if (!contact?.emails || contact.emails.length === 0) {
      toast.error('Contact must have an email address to reset password');
      return;
    }
    setShowResetPasswordModal(true);
  };

  const handleResetPasswordConfirm = async () => {
    try {
      setResettingPassword(true);
      await contactService.resetPassword(workspaceId, contactId);
      toast.success('Password has been reset and emailed to the contact');
      setShowResetPasswordModal(false);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to reset password';
      toast.error(errorMessage);
      console.error('Error resetting password:', err);
    } finally {
      setResettingPassword(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="contact-details-overlay" onClick={onClose} />
        <div className="contact-details-slider">
          <div className="contact-details-loading">
            <div className="contact-details-loading-spinner" />
          </div>
        </div>
      </>
    );
  }

  if (error || !contact) {
    return (
      <>
        <div className="contact-details-overlay" onClick={onClose} />
        <div className="contact-details-slider">
          <div className="contact-details-error">
            <p>{error || 'Contact not found'}</p>
            <button className="btn btn-secondary btn-md" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="contact-details-overlay" onClick={onClose} />
      <div className="contact-details-slider">
        {/* Header */}
        <div className="contact-details-header">
          <div className="contact-details-header-left">
            <button className="contact-details-back-btn" onClick={onClose}>
              ← Back
            </button>
            <div className="contact-details-header-content">
              <div className="contact-details-avatar">
                {contact.displayName?.charAt(0)?.toUpperCase() || 'C'}
              </div>
              <div className="contact-details-header-info">
                <h1 className="contact-details-name">{contact.displayName}</h1>
                <div className="contact-details-meta">
                  {contact.contactType.length > 0 && (
                    <div className="contact-details-types">
                      {contact.contactType.map((type, idx) => (
                        <span key={idx} className="contact-details-type-badge">
                          {type}
                        </span>
                      ))}
                    </div>
                  )}
                  <span className={`contact-details-status ${contact.isInactive ? 'inactive' : 'active'}`}>
                    {contact.isInactive ? 'Inactive' : 'Active'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="contact-details-header-actions">
            <button className="contact-details-close" onClick={onClose}>
              ✕
            </button>
          </div>
        </div>

        {/* 3-Column Layout */}
        <div className="contact-details-body">
          {/* Left Column - 25% */}
          <div className="contact-details-column contact-details-left">
            {/* Personal Information */}
            <div className="contact-details-tile">
              <div className="contact-details-tile-header">
                <h3 className="contact-details-tile-title">Personal Information</h3>
                {editingTile !== 'name' && (
                  <button
                    className="contact-details-tile-edit-btn"
                    onClick={() => handleEditTile('name', {
                      prefix: contact.prefix || '',
                      firstName: contact.firstName || '',
                      middleName: contact.middleName || '',
                      lastName: contact.lastName || '',
                      suffix: contact.suffix || '',
                      displayName: contact.displayName || ''
                    })}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}
                  >
                    <Pencil size={16} />
                  </button>
                )}
              </div>
              <div className="contact-details-tile-content">
                {editingTile === 'name' ? (
                  <>
                    <div className="contact-details-edit-field">
                      <label className="contact-details-edit-label">Prefix</label>
                      <input
                        type="text"
                        className="contact-details-edit-input"
                        value={editData?.prefix || ''}
                        onChange={(e) => setEditData({ ...editData, prefix: e.target.value })}
                        placeholder="e.g., Mr., Mrs., Dr."
                      />
                    </div>
                    <div className="contact-details-edit-field">
                      <label className="contact-details-edit-label">First Name</label>
                      <input
                        type="text"
                        className="contact-details-edit-input"
                        value={editData?.firstName || ''}
                        onChange={(e) => setEditData({ ...editData, firstName: e.target.value })}
                        placeholder="First name"
                      />
                    </div>
                    <div className="contact-details-edit-field">
                      <label className="contact-details-edit-label">Middle Name</label>
                      <input
                        type="text"
                        className="contact-details-edit-input"
                        value={editData?.middleName || ''}
                        onChange={(e) => setEditData({ ...editData, middleName: e.target.value })}
                        placeholder="Middle name (optional)"
                      />
                    </div>
                    <div className="contact-details-edit-field">
                      <label className="contact-details-edit-label">Last Name</label>
                      <input
                        type="text"
                        className="contact-details-edit-input"
                        value={editData?.lastName || ''}
                        onChange={(e) => setEditData({ ...editData, lastName: e.target.value })}
                        placeholder="Last name"
                      />
                    </div>
                    <div className="contact-details-edit-field">
                      <label className="contact-details-edit-label">Suffix</label>
                      <input
                        type="text"
                        className="contact-details-edit-input"
                        value={editData?.suffix || ''}
                        onChange={(e) => setEditData({ ...editData, suffix: e.target.value })}
                        placeholder="e.g., Jr., Sr., III"
                      />
                    </div>
                    <div className="contact-details-edit-field">
                      <label className="contact-details-edit-label">Display Name</label>
                      <input
                        type="text"
                        className="contact-details-edit-input"
                        value={editData?.displayName || ''}
                        onChange={(e) => setEditData({ ...editData, displayName: e.target.value })}
                        placeholder="Display name"
                      />
                    </div>
                    <div className="contact-details-edit-actions">
                      <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit}>
                        Cancel
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveTile} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {contact.prefix && (
                      <div className="contact-details-list-item">
                        <span className="contact-details-list-label">Prefix</span>
                        <span className="contact-details-list-value">{contact.prefix}</span>
                      </div>
                    )}
                    {contact.firstName && (
                      <div className="contact-details-list-item">
                        <span className="contact-details-list-label">First Name</span>
                        <span className="contact-details-list-value">{contact.firstName}</span>
                      </div>
                    )}
                    {contact.middleName && (
                      <div className="contact-details-list-item">
                        <span className="contact-details-list-label">Middle Name</span>
                        <span className="contact-details-list-value">{contact.middleName}</span>
                      </div>
                    )}
                    {contact.lastName && (
                      <div className="contact-details-list-item">
                        <span className="contact-details-list-label">Last Name</span>
                        <span className="contact-details-list-value">{contact.lastName}</span>
                      </div>
                    )}
                    {contact.suffix && (
                      <div className="contact-details-list-item">
                        <span className="contact-details-list-label">Suffix</span>
                        <span className="contact-details-list-value">{contact.suffix}</span>
                      </div>
                    )}
                    {contact.displayName && (
                      <div className="contact-details-list-item">
                        <span className="contact-details-list-label">Display Name</span>
                        <span className="contact-details-list-value">{contact.displayName}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="contact-details-tile">
              <div className="contact-details-tile-header">
                <h3 className="contact-details-tile-title">Contact Information</h3>
                {editingTile !== 'contact-info' && (
                  <button
                    className="contact-details-tile-edit-btn"
                    onClick={() => handleEditTile('contact-info', {
                      emails: contact.emails || [],
                      phones: contact.phones || [],
                      addresses: contact.addresses || []
                    })}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}
                  >
                    <Pencil size={16} />
                  </button>
                )}
              </div>
              <div className="contact-details-tile-content">
                {editingTile === 'contact-info' ? (
                  <>
                    {/* Emails Editor */}
                    <div className="contact-details-subsection">
                      <div className="contact-details-subsection-header">
                        <label className="contact-details-edit-label">Emails</label>
                        <button
                          type="button"
                          className="contact-details-add-btn-small"
                          onClick={() => setEditData({
                            ...editData,
                            emails: [...editData.emails, { type: 'Work', email: '', htmlSupported: true, marketing: false, isInactive: false, isPrimary: false }]
                          })}
                        >
                          + Add
                        </button>
                      </div>
                      {editData?.emails.map((email: any, index: number) => (
                        <div key={index} className="contact-details-array-item">
                          <div className="contact-details-edit-row">
                            <select
                              className="contact-details-edit-input-small"
                              value={email.type}
                              onChange={(e) => {
                                const newEmails = [...editData.emails];
                                newEmails[index].type = e.target.value;
                                setEditData({ ...editData, emails: newEmails });
                              }}
                            >
                              <option value="Work">Work</option>
                              <option value="Personal">Personal</option>
                              <option value="Other">Other</option>
                            </select>
                            <input
                              type="email"
                              className="contact-details-edit-input"
                              value={email.email}
                              onChange={(e) => {
                                const newEmails = [...editData.emails];
                                newEmails[index].email = e.target.value;
                                setEditData({ ...editData, emails: newEmails });
                              }}
                              placeholder="email@example.com"
                            />
                            <label className="contact-details-inline-checkbox">
                              <input
                                type="checkbox"
                                checked={email.isPrimary}
                                onChange={(e) => {
                                  const newEmails = editData.emails.map((em: any, i: number) => ({
                                    ...em,
                                    isPrimary: i === index ? e.target.checked : false
                                  }));
                                  setEditData({ ...editData, emails: newEmails });
                                }}
                              />
                              <span>Primary</span>
                            </label>
                            {editData.emails.length > 1 && (
                              <button
                                type="button"
                                className="contact-details-remove-btn-small"
                                onClick={() => {
                                  const newEmails = editData.emails.filter((_: any, i: number) => i !== index);
                                  setEditData({ ...editData, emails: newEmails });
                                }}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Phones Editor */}
                    <div className="contact-details-subsection">
                      <div className="contact-details-subsection-header">
                        <label className="contact-details-edit-label">Phones</label>
                        <button
                          type="button"
                          className="contact-details-add-btn-small"
                          onClick={() => setEditData({
                            ...editData,
                            phones: [...editData.phones, { type: 'Mobile', number: '', isInactive: false, isPrimary: false, smsOptIn: false }]
                          })}
                        >
                          + Add
                        </button>
                      </div>
                      {editData?.phones.map((phone: any, index: number) => (
                        <div key={index} className="contact-details-array-item">
                          <div className="contact-details-edit-row">
                            <select
                              className="contact-details-edit-input-small"
                              value={phone.type}
                              onChange={(e) => {
                                const newPhones = [...editData.phones];
                                newPhones[index].type = e.target.value;
                                setEditData({ ...editData, phones: newPhones });
                              }}
                            >
                              <option value="Mobile">Mobile</option>
                              <option value="Work">Work</option>
                              <option value="Home">Home</option>
                              <option value="Other">Other</option>
                            </select>
                            <input
                              type="tel"
                              className="contact-details-edit-input"
                              value={phone.number}
                              onChange={(e) => {
                                const newPhones = [...editData.phones];
                                newPhones[index].number = e.target.value;
                                setEditData({ ...editData, phones: newPhones });
                              }}
                              placeholder="Phone number"
                            />
                            <label className="contact-details-inline-checkbox">
                              <input
                                type="checkbox"
                                checked={phone.isPrimary}
                                onChange={(e) => {
                                  const newPhones = editData.phones.map((ph: any, i: number) => ({
                                    ...ph,
                                    isPrimary: i === index ? e.target.checked : false
                                  }));
                                  setEditData({ ...editData, phones: newPhones });
                                }}
                              />
                              <span>Primary</span>
                            </label>
                            {editData.phones.length > 1 && (
                              <button
                                type="button"
                                className="contact-details-remove-btn-small"
                                onClick={() => {
                                  const newPhones = editData.phones.filter((_: any, i: number) => i !== index);
                                  setEditData({ ...editData, phones: newPhones });
                                }}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Addresses Editor */}
                    <div className="contact-details-subsection">
                      <div className="contact-details-subsection-header">
                        <label className="contact-details-edit-label">Addresses</label>
                        <button
                          type="button"
                          className="contact-details-add-btn-small"
                          onClick={() => setEditData({
                            ...editData,
                            addresses: [...editData.addresses, {
                              type: 'Home',
                              street1: '',
                              street2: '',
                              city: '',
                              state: '',
                              zip: '',
                              country: '',
                              isPrimary: false,
                              isInactive: false
                            }]
                          })}
                        >
                          + Add
                        </button>
                      </div>
                      {editData?.addresses.map((address: any, index: number) => (
                        <div key={index} className="contact-details-array-item">
                          <div className="contact-details-address-edit">
                            <div className="contact-details-edit-row">
                              <select
                                className="contact-details-edit-input-small"
                                value={address.type}
                                onChange={(e) => {
                                  const newAddresses = [...editData.addresses];
                                  newAddresses[index].type = e.target.value;
                                  setEditData({ ...editData, addresses: newAddresses });
                                }}
                              >
                                <option value="Home">Home</option>
                                <option value="Work">Work</option>
                                <option value="Other">Other</option>
                              </select>
                              <label className="contact-details-inline-checkbox">
                                <input
                                  type="checkbox"
                                  checked={address.isPrimary}
                                  onChange={(e) => {
                                    const newAddresses = editData.addresses.map((addr: any, i: number) => ({
                                      ...addr,
                                      isPrimary: i === index ? e.target.checked : false
                                    }));
                                    setEditData({ ...editData, addresses: newAddresses });
                                  }}
                                />
                                <span>Primary</span>
                              </label>
                              {editData.addresses.length > 1 && (
                                <button
                                  type="button"
                                  className="contact-details-remove-btn-small"
                                  onClick={() => {
                                    const newAddresses = editData.addresses.filter((_: any, i: number) => i !== index);
                                    setEditData({ ...editData, addresses: newAddresses });
                                  }}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                            <input
                              type="text"
                              className="contact-details-edit-input"
                              value={address.street1}
                              onChange={(e) => {
                                const newAddresses = [...editData.addresses];
                                newAddresses[index].street1 = e.target.value;
                                setEditData({ ...editData, addresses: newAddresses });
                              }}
                              placeholder="Street 1"
                            />
                            <input
                              type="text"
                              className="contact-details-edit-input"
                              value={address.street2}
                              onChange={(e) => {
                                const newAddresses = [...editData.addresses];
                                newAddresses[index].street2 = e.target.value;
                                setEditData({ ...editData, addresses: newAddresses });
                              }}
                              placeholder="Street 2 (optional)"
                            />
                            <div className="contact-details-edit-row">
                              <input
                                type="text"
                                className="contact-details-edit-input"
                                value={address.city}
                                onChange={(e) => {
                                  const newAddresses = [...editData.addresses];
                                  newAddresses[index].city = e.target.value;
                                  setEditData({ ...editData, addresses: newAddresses });
                                }}
                                placeholder="City"
                              />
                              <input
                                type="text"
                                className="contact-details-edit-input-small"
                                value={address.state}
                                onChange={(e) => {
                                  const newAddresses = [...editData.addresses];
                                  newAddresses[index].state = e.target.value;
                                  setEditData({ ...editData, addresses: newAddresses });
                                }}
                                placeholder="State"
                              />
                              <input
                                type="text"
                                className="contact-details-edit-input-small"
                                value={address.zip}
                                onChange={(e) => {
                                  const newAddresses = [...editData.addresses];
                                  newAddresses[index].zip = e.target.value;
                                  setEditData({ ...editData, addresses: newAddresses });
                                }}
                                placeholder="ZIP"
                              />
                            </div>
                            <input
                              type="text"
                              className="contact-details-edit-input"
                              value={address.country}
                              onChange={(e) => {
                                const newAddresses = [...editData.addresses];
                                newAddresses[index].country = e.target.value;
                                setEditData({ ...editData, addresses: newAddresses });
                              }}
                              placeholder="Country"
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="contact-details-edit-actions">
                      <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit}>
                        Cancel
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveTile} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* View Mode */}
                    {contact.emails && contact.emails.length > 0 && (
                      <div className="contact-details-info-section">
                        <div className="contact-details-info-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Mail size={18} /> Emails
                        </div>
                        {contact.emails.map((email, idx) => (
                          <div key={idx} className="contact-details-info-item-compact">
                            <span className="contact-details-info-type">{email.type}</span>
                            <span className="contact-details-info-value-compact">{email.email}</span>
                            {email.isPrimary && <span className="contact-details-primary-badge">Primary</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {contact.phones && contact.phones.length > 0 && (
                      <div className="contact-details-info-section">
                        <div className="contact-details-info-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Phone size={18} /> Phones
                        </div>
                        {contact.phones.map((phone, idx) => (
                          <div key={idx} className="contact-details-info-item-compact">
                            <span className="contact-details-info-type">{phone.type}</span>
                            <span className="contact-details-info-value-compact">{phone.number}</span>
                            {phone.isPrimary && <span className="contact-details-primary-badge">Primary</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {contact.addresses && contact.addresses.length > 0 && (
                      <div className="contact-details-info-section">
                        <div className="contact-details-info-section-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <MapPin size={18} /> Addresses
                        </div>
                        {contact.addresses.map((address, idx) => (
                          <div key={idx} className="contact-details-info-item-compact">
                            <span className="contact-details-info-type">{address.type}</span>
                            <span className="contact-details-info-value-compact">{formatAddress(address)}</span>
                            {address.isPrimary && <span className="contact-details-primary-badge">Primary</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Contact Types */}
            <div className="contact-details-tile">
              <div className="contact-details-tile-header">
                <h3 className="contact-details-tile-title">Contact Types</h3>
                {editingTile !== 'contact-types' && (
                  <button
                    className="contact-details-tile-edit-btn"
                    onClick={() => handleEditTile('contact-types', { contactType: contact.contactType || [] })}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}
                  >
                    <Pencil size={16} />
                  </button>
                )}
              </div>
              <div className="contact-details-tile-content">
                {editingTile === 'contact-types' ? (
                  <>
                    <div className="contact-details-edit-field">
                      <label className="contact-details-edit-label">Contact Types</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {['End-User', 'Broker', 'Agent', 'Investor', 'Partner', 'Vendor', 'Other'].map((type) => (
                          <label key={type} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                              type="checkbox"
                              checked={editData?.contactType?.includes(type) || false}
                              onChange={(e) => {
                                const currentTypes = editData?.contactType || [];
                                const newTypes = e.target.checked
                                  ? [...currentTypes, type]
                                  : currentTypes.filter((t: string) => t !== type);
                                setEditData({ ...editData, contactType: newTypes });
                              }}
                            />
                            <span>{type}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className="contact-details-edit-actions">
                      <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit}>
                        Cancel
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveTile} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {contact.contactType && contact.contactType.length > 0 ? (
                      <div className="contact-details-tags">
                        {contact.contactType.map((type, idx) => (
                          <span key={idx} className="contact-details-tag contact-details-tag-list">
                            {type}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="contact-details-empty">No contact types assigned</p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Geography */}
            <div className="contact-details-tile">
              <div className="contact-details-tile-header">
                <h3 className="contact-details-tile-title">Geography</h3>
                {editingTile !== 'geography' && (
                  <button
                    className="contact-details-tile-edit-btn"
                    onClick={() => handleEditTile('geography', contact.geography || { country: '', state: '' })}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}
                  >
                    <Pencil size={16} />
                  </button>
                )}
              </div>
              <div className="contact-details-tile-content">
                {editingTile === 'geography' ? (
                  <>
                    <div className="contact-details-edit-field">
                      <label className="contact-details-edit-label">Country</label>
                      <input
                        type="text"
                        className="contact-details-edit-input"
                        value={editData?.country || ''}
                        onChange={(e) => setEditData({ ...editData, country: e.target.value })}
                        placeholder="e.g., Canada"
                      />
                    </div>
                    <div className="contact-details-edit-field">
                      <label className="contact-details-edit-label">State/Province</label>
                      <input
                        type="text"
                        className="contact-details-edit-input"
                        value={editData?.state || ''}
                        onChange={(e) => setEditData({ ...editData, state: e.target.value })}
                        placeholder="e.g., Ontario"
                      />
                    </div>
                    <div className="contact-details-edit-actions">
                      <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit}>
                        Cancel
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveTile} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {contact.geography?.country ? (
                      <div className="contact-details-list-item">
                        <span className="contact-details-list-label">Country</span>
                        <span className="contact-details-list-value">{contact.geography.country}</span>
                      </div>
                    ) : null}
                    {contact.geography?.state ? (
                      <div className="contact-details-list-item">
                        <span className="contact-details-list-label">State</span>
                        <span className="contact-details-list-value">{contact.geography.state}</span>
                      </div>
                    ) : null}
                    {!contact.geography?.country && !contact.geography?.state && (
                      <p className="contact-details-empty">No geography information</p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Communication Preferences */}
            <div className="contact-details-tile">
              <div className="contact-details-tile-header">
                <h3 className="contact-details-tile-title">Communication Preferences</h3>
                {editingTile !== 'communication' && (
                  <button
                    className="contact-details-tile-edit-btn"
                    onClick={() => handleEditTile('communication', {
                      doNotCall: contact.doNotCall || false,
                      doNotEmail: contact.doNotEmail || false,
                      doNotSMS: contact.doNotSMS || false
                    })}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}
                  >
                    <Pencil size={16} />
                  </button>
                )}
              </div>
              <div className="contact-details-tile-content">
                {editingTile === 'communication' ? (
                  <>
                    <div className="contact-details-edit-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={editData?.doNotCall || false}
                          onChange={(e) => setEditData({ ...editData, doNotCall: e.target.checked })}
                        />
                        <span>Do Not Call</span>
                      </label>
                    </div>
                    <div className="contact-details-edit-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={editData?.doNotEmail || false}
                          onChange={(e) => setEditData({ ...editData, doNotEmail: e.target.checked })}
                        />
                        <span>Do Not Email</span>
                      </label>
                    </div>
                    <div className="contact-details-edit-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={editData?.doNotSMS || false}
                          onChange={(e) => setEditData({ ...editData, doNotSMS: e.target.checked })}
                        />
                        <span>Do Not SMS</span>
                      </label>
                    </div>
                    <div className="contact-details-edit-actions">
                      <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit}>
                        Cancel
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveTile} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    {contact.doNotCall && (
                      <div className="contact-details-preference-badge contact-details-preference-warning" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Ban size={16} /> Do Not Call
                      </div>
                    )}
                    {contact.doNotEmail && (
                      <div className="contact-details-preference-badge contact-details-preference-warning" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Ban size={16} /> Do Not Email
                      </div>
                    )}
                    {contact.doNotSMS && (
                      <div className="contact-details-preference-badge contact-details-preference-warning" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Ban size={16} /> Do Not SMS
                      </div>
                    )}
                    {!contact.doNotCall && !contact.doNotEmail && !contact.doNotSMS && (
                      <p className="contact-details-empty">No restrictions</p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Portal Access */}
            <div className="contact-details-tile">
              <div className="contact-details-tile-header">
                <h3 className="contact-details-tile-title">Purchaser Portal</h3>
                {editingTile !== 'portal' && (
                  <button
                    className="contact-details-tile-edit-btn"
                    onClick={() => handleEditTile('portal', { allowLogin: contact.allowLogin || false })}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#48182F' }}
                  >
                    <Pencil size={16} />
                  </button>
                )}
              </div>
              <div className="contact-details-tile-content">
                {editingTile === 'portal' ? (
                  <>
                    <div className="contact-details-edit-checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={editData?.allowLogin || false}
                          onChange={(e) => setEditData({ ...editData, allowLogin: e.target.checked })}
                        />
                        <span>Allow portal access</span>
                      </label>
                    </div>
                    <div className="contact-details-edit-actions">
                      <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit}>
                        Cancel
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveTile} disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="contact-details-list-item">
                      <span className="contact-details-list-label">Status</span>
                      <span className={`contact-details-badge ${contact.allowLogin ? 'contact-details-badge-success' : 'contact-details-badge-gray'}`}>
                        {contact.allowLogin ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    {contact.lastLoginAt && (
                      <div className="contact-details-list-item">
                        <span className="contact-details-list-label">Last Login</span>
                        <span className="contact-details-list-value">
                          {new Date(contact.lastLoginAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {contact.allowLogin && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={handleResetPasswordClick}
                        disabled={resettingPassword}
                        style={{ marginTop: '12px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                      >
                        <Key size={16} />
                        {resettingPassword ? 'Resetting...' : 'Reset Password'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Middle Column - 50% */}
          <div className="contact-details-column contact-details-middle">
            {/* Notes */}
            <div className="contact-details-tile">
              <div className="contact-details-tile-header">
                <h3 className="contact-details-tile-title">Internal Notes</h3>
                {editingTile !== 'internal-notes' && (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleEditTile('internal-notes', { content: '' })}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    + Add Note
                  </button>
                )}
              </div>
              <div className="contact-details-tile-content">
                {editingTile === 'internal-notes' && (
                  <>
                    <div className="contact-details-edit-field">
                      <label className="contact-details-edit-label">Add Note</label>
                      <textarea
                        className="form-input"
                        value={editData?.content || ''}
                        onChange={(e) => setEditData({ ...editData, content: e.target.value })}
                        rows={5}
                        placeholder="Add internal notes..."
                        disabled={saving}
                        style={{ width: '100%', resize: 'vertical', minHeight: '100px' }}
                      />
                    </div>
                    <div className="contact-details-edit-actions">
                      <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit}>
                        Cancel
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSaveTile} disabled={saving}>
                        {saving ? 'Saving...' : 'Add'}
                      </button>
                    </div>
                    {notes.length > 0 && (
                      <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
                        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                          Previous Notes
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {notes.map((note) => (
                            <div key={note._id} style={{
                              padding: '12px',
                              backgroundColor: '#f9fafb',
                              borderRadius: '6px',
                              borderLeft: '3px solid #48182F'
                            }}>
                              <p style={{
                                whiteSpace: 'pre-wrap',
                                marginBottom: '8px',
                                color: '#374151',
                                fontSize: '14px',
                                lineHeight: '1.5'
                              }}>
                                {note.content}
                              </p>
                              <div style={{
                                fontSize: '12px',
                                color: '#6b7280',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <span style={{ fontWeight: '500' }}>
                                  {note.createdByName || note.createdBy}
                                </span>
                                <span>•</span>
                                <span>
                                  {new Date(note.createdAt).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
                {editingTile !== 'internal-notes' && (
                  <>
                    {notes.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {notes.map((note) => (
                          <div key={note._id} style={{
                            padding: '12px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '6px',
                            borderLeft: '3px solid #48182F'
                          }}>
                            <p style={{
                              whiteSpace: 'pre-wrap',
                              marginBottom: '8px',
                              color: '#374151',
                              fontSize: '14px',
                              lineHeight: '1.5'
                            }}>
                              {note.content}
                            </p>
                            <div style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <span style={{ fontWeight: '500' }}>
                                {note.createdByName || note.createdBy}
                              </span>
                              <span>•</span>
                              <span>
                                {new Date(note.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="contact-details-empty">No notes added yet</p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Activities - Placeholder */}
            <div className="contact-details-tile">
              <h3 className="contact-details-tile-title">Recent Activities</h3>
              <div className="contact-details-tile-content">
                <div className="contact-details-empty-state">
                  <div className="contact-details-empty-icon">
                    <ClipboardList size={48} style={{ color: '#9CA3AF' }} />
                  </div>
                  <p className="contact-details-empty-text">No activities yet</p>
                  <p className="contact-details-empty-subtext">Activities will appear here as they occur</p>
                </div>
              </div>
            </div>

            {/* Timeline - Placeholder */}
            <div className="contact-details-tile">
              <h3 className="contact-details-tile-title">Timeline</h3>
              <div className="contact-details-tile-content">
                <div className="contact-details-timeline">
                  <div className="contact-details-timeline-item">
                    <div className="contact-details-timeline-dot"></div>
                    <div className="contact-details-timeline-content">
                      <div className="contact-details-timeline-title">Contact Created</div>
                      <div className="contact-details-timeline-date">
                        {new Date(contact.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  {contact.updatedAt && contact.updatedAt !== contact.createdAt && (
                    <div className="contact-details-timeline-item">
                      <div className="contact-details-timeline-dot"></div>
                      <div className="contact-details-timeline-content">
                        <div className="contact-details-timeline-title">Last Updated</div>
                        <div className="contact-details-timeline-date">
                          {new Date(contact.updatedAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - 25% */}
          <div className="contact-details-column contact-details-right">
            {/* Quick Stats */}
            <div className="contact-details-tile">
              <h3 className="contact-details-tile-title">Quick Stats</h3>
              <div className="contact-details-tile-content">
                <div className="contact-details-stat">
                  <div className="contact-details-stat-value">{getActiveDealsCount()}</div>
                  <div className="contact-details-stat-label">Active Deals</div>
                </div>
                <div className="contact-details-stat">
                  <div className="contact-details-stat-value">
                    ${getTotalSpent().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="contact-details-stat-label">Total Spent</div>
                </div>
                <div className="contact-details-stat">
                  <div className="contact-details-stat-value">{getUnitsOwned()}</div>
                  <div className="contact-details-stat-label">Units Owned</div>
                </div>
              </div>
            </div>

            {/* Deals */}
            <div className="contact-details-tile">
              <h3 className="contact-details-tile-title">Deals</h3>
              <div className="contact-details-tile-content">
                {deals.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {deals.map((deal) => (
                      <div key={deal._id} style={{
                        padding: '10px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '13px',
                        position: 'relative'
                      }}>
                        <div style={{ fontWeight: '600', marginBottom: '4px', paddingRight: '24px' }}>
                          {deal.dealNumber || 'N/A'}
                        </div>
                        <div style={{ color: '#6b7280', marginBottom: '4px' }}>
                          {deal.unitNo || 'No unit'}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '4px',
                            backgroundColor: deal.stage === 'CANCELLED' || deal.stage === 'TERMINATED' ? '#fee2e2' : '#dcfce7',
                            color: deal.stage === 'CANCELLED' || deal.stage === 'TERMINATED' ? '#991b1b' : '#166534',
                            fontSize: '11px',
                            fontWeight: '500'
                          }}>
                            {deal.stage || 'NEW'}
                          </span>
                          {deal.finance?.totalPrice > 0 && (
                            <span style={{ fontWeight: '600', color: '#48182F' }}>
                              ${deal.finance.totalPrice.toLocaleString()}
                            </span>
                          )}
                        </div>
                        <a
                          href={`/workspaces/${workspaceId}/deals?view=${deal._id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            color: '#48182F',
                            display: 'flex',
                            alignItems: 'center',
                            textDecoration: 'none'
                          }}
                          title="Open deal in new tab"
                        >
                          <ExternalLink size={16} />
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="contact-details-empty-state-small">
                    <p className="contact-details-empty-text">No deals</p>
                  </div>
                )}
              </div>
            </div>

            {/* Units */}
            <div className="contact-details-tile">
              <h3 className="contact-details-tile-title">Units</h3>
              <div className="contact-details-tile-content">
                {deals.filter(deal => deal.unitId && !['CANCELLED', 'TERMINATED'].includes(deal.stage)).length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {deals
                      .filter(deal => deal.unitId && !['CANCELLED', 'TERMINATED'].includes(deal.stage))
                      .map((deal) => (
                        <div key={deal._id} style={{
                          padding: '8px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '13px',
                          position: 'relative'
                        }}>
                          <div style={{ fontWeight: '600', color: '#48182F', paddingRight: '24px' }}>
                            {deal.unitNo}
                          </div>
                          {deal.projectName && (
                            <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px' }}>
                              {deal.projectName}
                            </div>
                          )}
                          <a
                            href={`/workspaces/${workspaceId}/units?view=${deal.unitId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              color: '#48182F',
                              display: 'flex',
                              alignItems: 'center',
                              textDecoration: 'none'
                            }}
                            title="Open unit in new tab"
                          >
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="contact-details-empty-state-small">
                    <p className="contact-details-empty-text">No units assigned</p>
                  </div>
                )}
              </div>
            </div>

            {/* Lockers */}
            <div className="contact-details-tile">
              <h3 className="contact-details-tile-title">Lockers</h3>
              <div className="contact-details-tile-content">
                {deals.some(deal => deal.assignedLocker && deal.assignedLocker.length > 0) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {deals
                      .filter(deal => deal.assignedLocker && deal.assignedLocker.length > 0)
                      .flatMap(deal =>
                        deal.assignedLocker.map((locker: any, idx: number) => (
                          <div key={`${deal._id}-${idx}`} style={{
                            padding: '8px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}>
                            <div style={{ fontWeight: '600', color: '#48182F' }}>
                              {locker.lockerSpaceNumber || 'N/A'}
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px' }}>
                              {locker.lockerType || 'Unknown Type'}
                            </div>
                          </div>
                        ))
                      )}
                  </div>
                ) : (
                  <div className="contact-details-empty-state-small">
                    <p className="contact-details-empty-text">No lockers assigned</p>
                  </div>
                )}
              </div>
            </div>

            {/* Parking */}
            <div className="contact-details-tile">
              <h3 className="contact-details-tile-title">Parking</h3>
              <div className="contact-details-tile-content">
                {deals.some(deal => deal.assignedParking && deal.assignedParking.length > 0) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {deals
                      .filter(deal => deal.assignedParking && deal.assignedParking.length > 0)
                      .flatMap(deal =>
                        deal.assignedParking.map((parking: any, idx: number) => (
                          <div key={`${deal._id}-${idx}`} style={{
                            padding: '8px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}>
                            <div style={{ fontWeight: '600', color: '#48182F' }}>
                              {parking.parkingSpaceNumber || 'N/A'}
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px' }}>
                              {parking.parkingType || 'Unknown Type'}
                            </div>
                          </div>
                        ))
                      )}
                  </div>
                ) : (
                  <div className="contact-details-empty-state-small">
                    <p className="contact-details-empty-text">No parking assigned</p>
                  </div>
                )}
              </div>
            </div>

            {/* Bicycle */}
            <div className="contact-details-tile">
              <h3 className="contact-details-tile-title">Bicycle</h3>
              <div className="contact-details-tile-content">
                {deals.some(deal => deal.assignedBicycle && deal.assignedBicycle.length > 0) ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {deals
                      .filter(deal => deal.assignedBicycle && deal.assignedBicycle.length > 0)
                      .flatMap(deal =>
                        deal.assignedBicycle.map((bicycle: any, idx: number) => (
                          <div key={`${deal._id}-${idx}`} style={{
                            padding: '8px',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}>
                            <div style={{ fontWeight: '600', color: '#48182F' }}>
                              {bicycle.bicycleSpaceNumber || 'N/A'}
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px' }}>
                              {bicycle.bicycleType || 'Unknown Type'}
                            </div>
                          </div>
                        ))
                      )}
                  </div>
                ) : (
                  <div className="contact-details-empty-state-small">
                    <p className="contact-details-empty-text">No bicycle assigned</p>
                  </div>
                )}
              </div>
            </div>

            {/* Lists & Labels */}
            {(contact.lists && contact.lists.length > 0) || (contact.labels && contact.labels.length > 0) ? (
              <div className="contact-details-tile">
                <h3 className="contact-details-tile-title">Lists & Labels</h3>
                <div className="contact-details-tile-content">
                  {contact.lists && contact.lists.length > 0 && (
                    <div className="contact-details-tags">
                      {contact.lists.map((list, idx) => (
                        <span key={idx} className="contact-details-tag contact-details-tag-list">
                          {list}
                        </span>
                      ))}
                    </div>
                  )}
                  {contact.labels && contact.labels.length > 0 && (
                    <div className="contact-details-tags">
                      {contact.labels.map((label, idx) => (
                        <span key={idx} className="contact-details-tag contact-details-tag-label">
                          {label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Footer */}
        <div className="contact-details-footer">
          <button
            className="btn btn-danger btn-sm"
            onClick={() => setShowDeleteModal(true)}
          >
            Delete Contact
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="contact-details-modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="contact-details-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Contact</h3>
              <p>Are you sure you want to delete "{contact.displayName}"? This action cannot be undone.</p>
              <div className="contact-details-modal-actions">
                <button
                  className="btn btn-secondary btn-md"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger btn-md"
                  onClick={handleDeleteContact}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reset Password Confirmation Modal */}
        {showResetPasswordModal && (
          <div className="contact-details-modal-overlay" onClick={() => !resettingPassword && setShowResetPasswordModal(false)}>
            <div className="contact-details-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Reset Password</h3>
              <p>Are you sure you want to reset the password for <strong>{contact.displayName}</strong>? A new password will be emailed to them.</p>
              <div className="contact-details-modal-actions">
                <button
                  className="btn btn-secondary btn-md"
                  onClick={() => setShowResetPasswordModal(false)}
                  disabled={resettingPassword}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary btn-md"
                  onClick={handleResetPasswordConfirm}
                  disabled={resettingPassword}
                >
                  {resettingPassword ? 'Resetting...' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Change History FAB Button */}
        <ChangeHistoryButton
          onClick={() => setHistoryDrawerOpen(true)}
          isOpen={historyDrawerOpen}
        />

        {/* Change History Drawer */}
        <ChangeHistoryDrawer
          isOpen={historyDrawerOpen}
          onClose={() => setHistoryDrawerOpen(false)}
          workspaceId={workspaceId}
          entityType="contact"
          entityId={contactId}
        />
      </div>
    </>
  );
};

export default ContactDetailsSlider;
